import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addAdminTaskApplicantsToEmailGroup,
  bulkUpdateAdminTaskApplicantStatus,
  createAdminEmailGroupFromTaskApplicants,
  exportAdminTaskApplicantsExcel,
  getAdminTaskApplicants,
  updateAdminTaskApplicantStatus,
} from '../services/adminService';

const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

const statusOptions = [
  { label: 'All statuses', value: '' },
  { label: 'Submitted', value: 'submitted' },
  { label: 'Reviewed', value: 'reviewed' },
  { label: 'Shortlisted', value: 'shortlisted' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Selected', value: 'selected' },
];

const bulkStatuses = [
  { label: 'Mark Reviewed', value: 'reviewed' },
  { label: 'Mark Shortlisted', value: 'shortlisted' },
  { label: 'Mark Rejected', value: 'rejected' },
  { label: 'Mark Selected', value: 'selected' },
];

const initialFilters = {
  search: '',
  status: '',
  position: '',
  groupId: '',
  dateFrom: '',
  dateTo: '',
  datePreset: '',
  sort: 'newest',
  page: 1,
  limit: 50,
};

function formatDate(value) {
  if (!value) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function formatLabel(value) {
  return String(value || '')
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatAnswer(value) {
  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : 'Not provided';
  }

  return value || 'Not provided';
}

function fileUrl(file) {
  return file?.url?.startsWith('http') ? file.url : `${apiBaseUrl}${file.url || ''}`;
}

function getDownloadFilename(contentDisposition) {
  const fallback = 'factorstreet-task-applicants-selected.xlsx';
  const match = String(contentDisposition || '').match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallback;
}

function compactFilters(filters) {
  const params = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== '' && value !== null && value !== undefined) {
      params[key] = value;
    }
  });

  return params;
}

function AdminTaskApplicants() {
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [submissions, setSubmissions] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ positions: [], groups: [] });
  const [counts, setCounts] = useState({
    filteredCount: 0,
    page: 1,
    totalSubmissions: 0,
    limit: 50,
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [expandedIds, setExpandedIds] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [newGroup, setNewGroup] = useState({ name: '', description: '' });
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [loading, setLoading] = useState(true);

  const selectedVisibleCount = useMemo(
    () => submissions.filter((submission) => selectedIds.includes(submission._id)).length,
    [submissions, selectedIds]
  );
  const totalPages = Math.max(Math.ceil((counts.filteredCount || 0) / (counts.limit || 50)), 1);
  const allVisibleSelected = submissions.length > 0 && submissions.every((submission) => selectedIds.includes(submission._id));

  const loadSubmissions = useCallback(async () => {
    const data = await getAdminTaskApplicants(compactFilters(appliedFilters));
    setSubmissions(data.submissions || []);
    setFilterOptions(data.filterOptions || { positions: [], groups: [] });
    setCounts({
      filteredCount: data.filteredCount || 0,
      page: data.page || 1,
      totalSubmissions: data.totalSubmissions || 0,
      limit: data.limit || 50,
    });
    setSelectedIds((current) => current.filter((id) => (data.submissions || []).some((submission) => submission._id === id)));
  }, [appliedFilters]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    loadSubmissions()
      .then(() => {
        if (isMounted) setLoading(false);
      })
      .catch(() => {
        if (isMounted) {
          setLoading(false);
          setFeedback({ type: 'error', message: 'Unable to load task applicants.' });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [loadSubmissions]);

  const updateFilter = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({
      ...current,
      [name]: value,
      page: 1,
      ...(name === 'dateFrom' || name === 'dateTo' ? { datePreset: '' } : {}),
    }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setSelectedIds([]);
  };

  const refreshSubmissions = () => {
    setFeedback({ type: '', message: '' });
    loadSubmissions().catch(() => setFeedback({ type: 'error', message: 'Unable to refresh task applicants.' }));
  };

  const setDatePreset = (datePreset) => {
    setFilters((current) => ({ ...current, datePreset, dateFrom: '', dateTo: '', page: 1 }));
  };

  const goToPage = (page) => {
    const next = { ...appliedFilters, page };
    setFilters(next);
    setAppliedFilters(next);
  };

  const toggleSelected = (submissionId) => {
    setSelectedIds((current) =>
      current.includes(submissionId)
        ? current.filter((id) => id !== submissionId)
        : [...current, submissionId]
    );
  };

  const toggleDetails = (submissionId) => {
    setExpandedIds((current) =>
      current.includes(submissionId)
        ? current.filter((id) => id !== submissionId)
        : [...current, submissionId]
    );
  };

  const toggleVisibleSelection = () => {
    const visibleIds = submissions.map((submission) => submission._id);
    setSelectedIds((current) =>
      allVisibleSelected
        ? current.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...current, ...visibleIds]))
    );
  };

  const runBulkStatus = async (status) => {
    if (selectedIds.length === 0) {
      setFeedback({ type: 'error', message: 'Select at least one task applicant.' });
      return;
    }

    try {
      await bulkUpdateAdminTaskApplicantStatus(selectedIds, status);
      await loadSubmissions();
      setFeedback({ type: 'success', message: `Updated ${selectedIds.length} task applicant(s).` });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Unable to update status.' });
    }
  };

  const addToExistingGroup = async () => {
    if (!selectedGroupId || selectedIds.length === 0) {
      setFeedback({ type: 'error', message: 'Select task applicants and choose a group.' });
      return;
    }

    try {
      const result = await addAdminTaskApplicantsToEmailGroup(selectedGroupId, selectedIds);
      await loadSubmissions();
      setFeedback({
        type: 'success',
        message: `Added ${result.addedCount} emails to ${result.group?.slug || result.group?.name}. Skipped ${result.skippedExistingCount} already existing in this group. Skipped ${result.skippedInvalidCount} missing/invalid email.`,
      });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Unable to add task applicants to group.' });
    }
  };

  const createGroupFromSelected = async (event) => {
    event.preventDefault();
    if (selectedIds.length === 0) {
      setFeedback({ type: 'error', message: 'Select at least one task applicant.' });
      return;
    }

    try {
      const result = await createAdminEmailGroupFromTaskApplicants({
        ...newGroup,
        submissionIds: selectedIds,
      });
      await loadSubmissions();
      setSelectedGroupId(result.group?._id || '');
      setNewGroup({ name: '', description: '' });
      setShowCreateGroup(false);
      setFeedback({
        type: 'success',
        message: `Created ${result.group?.name}. Added ${result.addedCount} emails. Skipped ${result.skippedExistingCount} duplicates and ${result.skippedInvalidCount} invalid emails.`,
      });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Unable to create group.' });
    }
  };

  const changeSubmissionStatus = async (submission, status, notes = submission.notes || '') => {
    const result = await updateAdminTaskApplicantStatus(submission._id, status, notes);
    const nextSubmission = {
      ...result.submission,
      emailGroups: submission.emailGroups || [],
    };
    setSubmissions((current) =>
      current.map((item) => (item._id === submission._id ? nextSubmission : item))
    );
  };

  const updateSubmissionNotesDraft = (submissionId, notes) => {
    setSubmissions((current) =>
      current.map((submission) =>
        submission._id === submissionId ? { ...submission, notes } : submission
      )
    );
  };

  const downloadExcel = async () => {
    if (selectedIds.length === 0) {
      setFeedback({ type: 'error', message: 'Please select at least one task applicant to download.' });
      return;
    }

    setDownloadingExcel(true);
    setFeedback({ type: '', message: '' });

    try {
      const response = await exportAdminTaskApplicantsExcel(selectedIds);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getDownloadFilename(response.headers?.['content-disposition']);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setFeedback({ type: 'success', message: `Excel export prepared for ${selectedIds.length} task applicant(s).` });
    } catch (error) {
      setFeedback({ type: 'error', message: 'Unable to download selected task applicants.' });
    } finally {
      setDownloadingExcel(false);
    }
  };

  return (
    <section className="admin-page admin-applications-page admin-task-applicants-page">
      <div className="admin-page__header">
        <p className="eyebrow">Task applicants / Hiring operations</p>
        <h1>Task Applicants</h1>
        <p>Review task submissions, update hiring status, and add selected candidates to email groups.</p>
      </div>

      {feedback.message ? (
        <p className={`form-alert form-alert--${feedback.type === 'error' ? 'error' : 'success'}`}>
          {feedback.message}
        </p>
      ) : null}

      <form className="admin-panel application-filter-panel" onSubmit={applyFilters}>
        <div className="admin-page__header--split application-count-bar">
          <div>
            <h2>{counts.filteredCount} / {counts.totalSubmissions} task applicants</h2>
            <span>{selectedIds.length} selected</span>
          </div>
          <div className="admin-bulk-actions">
            <button type="submit">Apply Filters</button>
            <button onClick={clearFilters} type="button">Clear Filters</button>
            <button onClick={refreshSubmissions} type="button">Refresh</button>
          </div>
        </div>

        <div className="admin-application-filter-grid">
          <label className="application-search-filter">
            Search
            <input
              name="search"
              onChange={updateFilter}
              placeholder="Search name, email, position, or task answers"
              type="search"
              value={filters.search}
            />
          </label>
          <label>
            Status
            <select name="status" onChange={updateFilter} value={filters.status}>
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Applied Position
            <select name="position" onChange={updateFilter} value={filters.position}>
              <option value="">All positions</option>
              {filterOptions.positions?.map((position) => (
                <option key={position.title} value={position.title}>
                  {position.title} ({position.count})
                </option>
              ))}
            </select>
          </label>
          <label>
            Group
            <select name="groupId" onChange={updateFilter} value={filters.groupId}>
              <option value="">All groups</option>
              <option value="none">Not in any group</option>
              {filterOptions.groups?.map((group) => (
                <option key={group._id} value={group._id}>
                  {group.name} ({group.count})
                </option>
              ))}
            </select>
          </label>
          <label>
            From date
            <input name="dateFrom" onChange={updateFilter} type="date" value={filters.dateFrom} />
          </label>
          <label>
            To date
            <input name="dateTo" onChange={updateFilter} type="date" value={filters.dateTo} />
          </label>
          <label>
            Sort
            <select name="sort" onChange={updateFilter} value={filters.sort}>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="name">Name A-Z</option>
            </select>
          </label>
        </div>

        <div className="application-date-actions">
          <button className={filters.datePreset === 'today' ? 'is-active' : ''} onClick={() => setDatePreset('today')} type="button">
            Today
          </button>
          <button className={filters.datePreset === 'last-7-days' ? 'is-active' : ''} onClick={() => setDatePreset('last-7-days')} type="button">
            Last 7 days
          </button>
          <button className={filters.datePreset === 'last-30-days' ? 'is-active' : ''} onClick={() => setDatePreset('last-30-days')} type="button">
            Last 30 days
          </button>
          <button onClick={() => setFilters((current) => ({ ...current, datePreset: '', dateFrom: '', dateTo: '', page: 1 }))} type="button">
            Clear date filter
          </button>
        </div>

        <div className="application-active-chips">
          {Object.entries(compactFilters(appliedFilters))
            .filter(([key]) => !['page', 'limit'].includes(key))
            .map(([key, value]) => (
              <span key={key}>{formatLabel(key)}: {value}</span>
            ))}
        </div>
      </form>

      <section className="admin-panel application-group-actions">
        <div className="admin-page__header--split">
          <div>
            <h2>Group selected task applicants</h2>
            <span>{selectedIds.length} selected, {selectedVisibleCount} visible on this page</span>
          </div>
          <button className="btn btn--secondary" onClick={() => setShowCreateGroup((current) => !current)} type="button">
            Create new group from selected
          </button>
        </div>
        <div className="application-group-action-row">
          <select onChange={(event) => setSelectedGroupId(event.target.value)} value={selectedGroupId}>
            <option value="">Select existing group</option>
            {filterOptions.groups?.map((group) => (
              <option key={group._id} value={group._id}>
                {group.name} ({group.count})
              </option>
            ))}
          </select>
          <button className="btn btn--primary" onClick={addToExistingGroup} type="button">
            Add selected to group
          </button>
        </div>
        {showCreateGroup ? (
          <form className="application-create-group-form" onSubmit={createGroupFromSelected}>
            <label>
              Group name
              <input
                onChange={(event) => setNewGroup((current) => ({ ...current, name: event.target.value }))}
                placeholder="task-round-27-may"
                value={newGroup.name}
              />
            </label>
            <label>
              Description
              <input
                onChange={(event) => setNewGroup((current) => ({ ...current, description: event.target.value }))}
                placeholder="Optional note for the email desk"
                value={newGroup.description}
              />
            </label>
            <button className="btn btn--primary" disabled={!newGroup.name.trim()} type="submit">
              Create group
            </button>
          </form>
        ) : null}
      </section>

      <div className="admin-panel admin-applications-list-panel">
        <div className="admin-page__header--split">
          <div>
            <h2>Submitted Task Forms</h2>
            <span>{loading ? 'Loading task applicants...' : `${submissions.length} shown on this page`}</span>
            <span className="application-selection-help">Select all selects visible task applicants on this page only.</span>
          </div>
          <div className="admin-bulk-actions">
            <button onClick={toggleVisibleSelection} type="button">
              {allVisibleSelected ? 'Clear page selection' : 'Select all'}
            </button>
            {bulkStatuses.map((action) => (
              <button key={action.value} onClick={() => runBulkStatus(action.value)} type="button">
                {action.label}
              </button>
            ))}
            <button disabled={downloadingExcel} onClick={downloadExcel} type="button">
              {downloadingExcel ? 'Downloading...' : 'Download Excel'}
            </button>
          </div>
        </div>

        <div className="application-card-list">
          {submissions.map((submission) => (
            <article
              className={`application-card-shell ${selectedIds.includes(submission._id) ? 'is-selected' : ''}`}
              key={submission._id}
            >
              <div className="application-card">
                <label className="application-card__check">
                  <input
                    checked={selectedIds.includes(submission._id)}
                    onChange={() => toggleSelected(submission._id)}
                    type="checkbox"
                  />
                </label>
                <div className="application-card__main">
                  <strong>{submission.candidateName || 'Unnamed candidate'}</strong>
                  <span>{submission.normalizedEmail || submission.candidateEmail || 'No email'}</span>
                  <span>{submission.appliedPosition || 'Open application'}</span>
                </div>
                <div className="application-card__meta">
                  <span>Original status: {formatLabel(submission.originalApplicationStatus)}</span>
                  <span>Task status: {formatLabel(submission.status)}</span>
                  <span>Files: {submission.uploadedFiles?.length || 0}</span>
                  <span>{formatDate(submission.createdAt)}</span>
                </div>
                <div className="application-card__groups">
                  {submission.emailGroups?.length ? (
                    submission.emailGroups.slice(0, 3).map((group) => <span key={group._id}>{group.name}</span>)
                  ) : (
                    <span>Not in any group</span>
                  )}
                </div>
                <button className="btn btn--secondary" onClick={() => toggleDetails(submission._id)} type="button">
                  {expandedIds.includes(submission._id) ? 'Hide details' : 'View details'}
                </button>
              </div>

              {expandedIds.includes(submission._id) ? (
                <div className="application-inline-detail">
                  <div className="lead-detail-grid">
                    <span>Email</span>
                    <strong>{submission.normalizedEmail || submission.candidateEmail || 'Not provided'}</strong>
                    <span>Applied Position</span>
                    <strong>{submission.appliedPosition || 'Open application'}</strong>
                    <span>Original Application Status</span>
                    <strong>{formatLabel(submission.originalApplicationStatus)}</strong>
                    <span>Task Status</span>
                    <select
                      onChange={(event) => changeSubmissionStatus(submission, event.target.value)}
                      value={submission.status}
                    >
                      {statusOptions
                        .filter((option) => option.value)
                        .map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                    </select>
                    <span>Original Application</span>
                    <strong>{submission.applicationId || 'Not linked'}</strong>
                    <span>Submitted</span>
                    <strong>{formatDate(submission.createdAt)}</strong>
                    <span>Groups</span>
                    <strong>
                      {submission.emailGroups?.length
                        ? submission.emailGroups.map((group) => group.name).join(', ')
                        : 'Not in any group'}
                    </strong>
                  </div>

                  <label className="application-notes">
                    Admin notes
                    <textarea
                      onBlur={(event) => changeSubmissionStatus(submission, submission.status, event.target.value)}
                      onChange={(event) => updateSubmissionNotesDraft(submission._id, event.target.value)}
                      rows="4"
                      value={submission.notes || ''}
                    />
                  </label>

                  <div className="application-response-groups">
                    {Object.entries(
                      (submission.responses || []).reduce((groups, response) => {
                        const key = response.categoryTitle || 'Other';
                        groups[key] = groups[key] || [];
                        groups[key].push(response);
                        return groups;
                      }, {})
                    ).map(([categoryTitle, responses]) => (
                      <section className="application-response-group" key={categoryTitle}>
                        <h3>{categoryTitle}</h3>
                        {responses.map((response) => (
                          <div className="application-response-row" key={`${response.fieldKey}-${response.fieldLabel}`}>
                            <span>{response.fieldLabel}</span>
                            {response.fileUrl ? (
                              <a href={fileUrl({ url: response.fileUrl })} rel="noreferrer" target="_blank">
                                {response.originalFileName || 'Download file'}
                              </a>
                            ) : (
                              <strong>{formatAnswer(response.value)}</strong>
                            )}
                          </div>
                        ))}
                      </section>
                    ))}
                  </div>

                  {submission.uploadedFiles?.length ? (
                    <div className="application-files">
                      <h3>Uploaded task files</h3>
                      {submission.uploadedFiles.map((file) => (
                        <a href={fileUrl(file)} key={file.storedName} rel="noreferrer" target="_blank">
                          {file.fieldLabel}: {file.originalName}
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </article>
          ))}
          {loading ? <p className="admin-empty">Loading task applicants...</p> : null}
          {!loading && submissions.length === 0 ? <p className="admin-empty">No task submissions found.</p> : null}
        </div>

        <div className="application-pagination">
          <button disabled={counts.page <= 1} onClick={() => goToPage(counts.page - 1)} type="button">
            Previous
          </button>
          <span>Page {counts.page} of {totalPages}</span>
          <button disabled={counts.page >= totalPages} onClick={() => goToPage(counts.page + 1)} type="button">
            Next
          </button>
        </div>
      </div>
    </section>
  );
}

export default AdminTaskApplicants;
